<template>
  <m-step-layout title="Payment Instructions" class="mt-16" type="sm">
      <template #description>
        <!-- eslint-disable-next-line max-len -->
        <p class="text-base text-gray-700">Please use our rule building wizard to create the payment instructions.</p>
        <!-- eslint-disable-next-line max-len -->
        <p class="text-sm text-gray-600 mt-2">Each rule has several parts - the account(s) from which payments will be made, the way in which those payments should be treated (eg lump sums or pensions), if applicable the way in which they should be divided between multiple accounts and finally, when the rule ends and the next rule applies.</p>
      </template>

      <m-form>
        <div
          v-if="rules.length === 0"
          class="flex flex-col items-center justify-center mt-4"
        >
          <button
            class="focus:outline-none"
            @click="showPaymentInstructionAdd = true"
            data-test="add-instruction"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-20 w-20" viewBox="0 0 20 20" fill="currentColor">
            <!-- eslint-disable-next-line max-len -->
              <path fill-rule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V8z" clip-rule="evenodd" />
            </svg>
            <p class="font-bold">Add Rule</p>
          </button>
        </div>
        <div v-else>
          <div class="mt-4 py-3 grid grid-cols-12 bg-gray-100 rounded-t-lg border border-b-0">
            <p class="col-span-3 col-start-2 uppercase text-sm font-medium">From</p>
            <p class="col-span-3 uppercase text-sm font-medium">Rule</p>
            <p class="col-span-4 uppercase text-sm font-medium">Until</p>
          </div>
          <div class="border rounded-b-lg overflow-hidden">
            <div v-for="(rule, idx) in rules" :key="idx">
              <payment-instruction
                v-if="rule.members !== undefined"
                :instruction="rule"
                :class="{ 'bg-gray-100': idx % 2}"
                @up="handleUp(idx)"
                @down="handleDown(idx)"
                @delete="handleDelete(idx)"
                @edit="handleEdit(idx)"
              ></payment-instruction>
              <div v-if="typeof rule === 'string'">
                <div class="py-2 grid grid-cols-12 items-center">
                  <div class="col-span-1 flex flex-col">
                    <button
                      class="flex justify-center focus:outline-none"
                      @click="handleUp(idx)"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M5 15l7-7 7 7"
                        />
                      </svg>
                    </button>
                    <button
                      class="flex justify-center focus:outline-none"
                      @click="handleDown(idx)"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                  </div>
                  <div class="col-span-3 text-sm">
                    <p>(Custom Rule)</p>
                  </div>
                  <div class="col-span-8 pr-2 text-sm">
                    <p>{{ rule }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="mt-4 flex justify-end">
            <m-button
              @click="showPaymentInstructionAdd = true"
              data-test="add-instruction"
            >Add Rule</m-button>
          </div>
        </div>
        <m-modal
          isSidebar
          size="sm"
          :show="showPaymentInstructionAdd"
          @close="showPaymentInstructionAdd = false"
          sidebarHeaderText="Add Payment Instruction"
        >
          <payment-instruction-add
            v-if="showPaymentInstructionAdd"
            @rule="handleRule"
            @close="handleClose"
            :edit-rule="editRule"
          ></payment-instruction-add>
        </m-modal>
        <m-modal
          :show="showPaymentInstructionWarning"
          @close="showPaymentInstructionWarning = false"
          sidebarHeaderText="Benefit Payment Instruction Warnings"
        >
          <payment-instruction-output  />
          <div class="mt-4 flex justify-end space-x-4">
            <m-button
              variant="secondary"
              @click="showPaymentInstructionWarning = false"
            >Cancel</m-button>
            <m-button
              data-test="btn-continue"
              @click="handleWarningContinue"
            >Continue</m-button>
          </div>
        </m-modal>
      </m-form>
      <div class="mt-6 text-right">
        <m-button
          class="ml-2"
          @click="handleBackStep"
          ref="back-btn"
        >Back</m-button>
        <m-button
          class="ml-2"
          @click="handleNextStep"
          ref="next-btn"
          data-test="btn-next"
          :disabled="this.rules.length === 0"
          :class="{ 'cursor-not-allowed': this.rules.length === 0 }"
        >Continue</m-button>
      </div>
    </m-step-layout>

</template>

<script>
import PensionInstructionMixin from '@/app/mixins/toolkit/pension-instructions/instruction';
import { arrayMove } from '@/app/utils/utils';

import PaymentInstruction from './payment-instruction.vue';
import PaymentInstructionOutput from './payment-instruction-output.vue';
import PaymentInstructionAdd from './payment-instruction-add/payment-instruction-add.vue';

export default {
  name: 'payment-instructions',
  mixins: [PensionInstructionMixin],
  components: {
    PaymentInstruction,
    PaymentInstructionOutput,
    PaymentInstructionAdd,
  },
  data() {
    return {
      showPaymentInstructionAdd: false,
      showPaymentInstructionWarning: false,
      rules: [],
      editRule: {},
    };
  },
  computed: {
    seenWarning: {
      get() {
        return this.getItem.seenWarning;
      },
      set(value) {
        this.setItem({ seenWarning: value });
      },
    },
  },
  methods: {
    handleRule({ type, rule, idx }) {
      if (type === 'add') {
        this.rules.push(rule);
      } else if (type === 'edit') {
        this.rules.splice(idx, 1, rule);
      }
      this.setItem({ rules: this.rules, replace: true });
      this.setItem({ seenWarning: false });
      this.handleClose();
    },
    handleClose() {
      this.showPaymentInstructionAdd = false;
      this.editRule = {};
    },
    handleEdit(num) {
      const [rule] = this.rules.filter((_rule, idx) => idx === num);
      this.editRule = {
        idx: num,
        rule,
      };
      this.showPaymentInstructionAdd = true;
    },
    handleDelete(num) {
      const { rules } = this;
      const filtered = rules.filter((_rule, idx) => idx !== num);
      this.rules = [...filtered];
      this.setItem({ rules: this.rules, replace: true });
      this.setItem({ seenWarning: false });
    },
    handleUp(idx) {
      if (idx === 0) return;
      this.rules = arrayMove(this.rules, idx, idx - 1);
      this.setItem({ rules: this.rules, replace: true });
    },
    handleDown(idx) {
      if ((idx + 1) === this.rules.length) return;
      this.rules = arrayMove(this.rules, idx, idx + 1);
      this.setItem({ rules: this.rules, replace: true });
    },
    async handleBackStep() {
      await this.setItem({ rules: this.rules, replace: true });
      this.$emit('backStep');
    },
    async handleNextStep() {
      if (!this.seenWarning) {
        await this.setItem({ rules: this.rules, replace: true });
        this.showPaymentInstructionWarning = true;
      } else {
        await this.setItem({ rules: this.rules, replace: true });
        this.$emit('nextStep');
      }
    },
    async handleWarningContinue() {
      this.showPaymentInstructionWarning = false;
      await this.setItem({ seenWarning: true });
      await this.handleNextStep();
    },
  },
  activated() {
    this.rules = this.getItem.rules;
  },
};
</script>
